import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import JsSIP from "jssip";
import { AuthContext } from "./auth";
import {
    getSoftphoneCredentials,
    getSoftphoneConfig,
    saveSoftphoneConfig as apiSaveConfig,
    getSoftphoneGlobalConfig,
    saveSoftphoneGlobalConfig as apiSaveGlobalConfig,
    getSoftphoneRamalStatus,
    heartbeatSoftphone,
    lookupSoftphoneCliente,
    registrarSoftphoneChamada,
    atualizarSoftphoneChamada,
    transcreverSoftphoneChamada,
} from "../services/api";

JsSIP.debug.disable();

// Extrai o número de quem está ligando (BINA). O SBC da NVOIP coloca o RAMAL DE
// DESTINO no From.uri.user (ex.: 116364002), então ler o From dá sempre o
// próprio ramal. O telefone real do chamador vem no display_name do From ou nos
// headers proprietários (P-CALLERID etc.). Mesma ordem do worker do bot de
// ligação (node_workers/bot_ligacao/src/sip-ua.js), que captura o BINA certo.
const extractCallerNumber = (request, ramalLocal = "") => {
    const ramal = String(ramalLocal || "").replace(/\D/g, "");

    const tryParse = (val) => {
        if (!val) return null;
        const s = String(val);
        // "tel:+55..." ou "<sip:NUM@host>"
        const m = s.match(/(?:tel:|sip:)\+?(\d{8,})/i);
        if (m) return m[1];
        // número solto, com ou sem formatação: '"+55 32 99822-8846"'
        const digitos = s.replace(/\D+/g, "");
        return digitos.length >= 8 ? digitos : null;
    };

    const safeHeader = (name) => {
        try { return request.getHeader?.(name); } catch (_) { return null; }
    };

    // O próprio ramal é o destino, não quem ligou — descarta e tenta a fonte seguinte.
    const aceita = (num) => (num && num !== ramal ? num : null);

    // 1) display_name do From: é aí que a NVOIP põe o telefone do chamador.
    const dnNum = aceita(tryParse(request?.from?.display_name));
    if (dnNum) return dnNum;

    // 2) headers proprietários da NVOIP.
    const headersOrdem = [
        "P-CALLERID",
        "Remote-Party-ID",
        "P-Asserted-Identity",
        "X-Caller-Id",
        "X-Caller-Number",
        "X-Original-Calling-Number",
        "Diversion",
    ];
    for (const h of headersOrdem) {
        const num = aceita(tryParse(safeHeader(h)));
        if (num) return num;
    }

    // 3) Último recurso: From.uri.user (normalmente o ramal de destino).
    const fromUser = String(request?.from?.uri?.user || "").replace(/\D/g, "");
    return fromUser.length >= 8 ? fromUser : "";
};

// Quem grava a ligação, por direção. No modo PBX a ENTRADA passa pelo nosso
// Asterisk e o MixMonitor grava tudo, com ou sem aba aberta. A SAÍDA ainda sai
// pela operadora enquanto não existir tronco nosso: lá o servidor não vê a
// mídia, então quem grava continua sendo o navegador. Backend antigo (sem a
// chave de saída) cai no comportamento de antes.
const gravaNoNavegador = (creds, direction) => {
    if (!creds) return true;
    return direction === "outgoing"
        ? creds.gravar_no_navegador_saida !== false
        : creds.gravar_no_navegador !== false;
};

// Quanto esperar, depois do primeiro candidato ICE, antes de mandar o SDP de
// qualquer jeito. Curto de propósito: o atendente sente essa espera.
const ICE_ESPERA_MS = 1500;

const PC_CONFIG = {
    iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
    ],
};

const DEFAULT_CONFIG = {
    modo_recepcao: "aba_ativa",
    auto_conectar_ao_abrir: false,
    auto_conectar_horario: false,
    horario_inicio: "08:00",
    horario_fim: "18:00",
    dias_semana: [1, 2, 3, 4, 5],
    tentar_reconectar: true,
    heartbeat_seg: 15,
    notificacao_browser: true,
    notificacao_som: true,
    volume_toque: 70,
    volume_chamada: 90,
    microfone_device_id: null,
    autofalante_device_id: null,
    mostrar_popup_cliente: true,
    auto_atender: false,
    auto_atender_apos_seg: 0,
    widget_posicao: { x: null, y: null },
};

const DEFAULT_GLOBAL_CONFIG = {
    transcrever_auto: false,
    idioma_transcricao: "pt",
    aceitou_termos_gravacao: false,
};

const normalizeNumber = (raw) => String(raw || "").replace(/\D/g, "");

const horaAgora = () => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
};

const horarioParaMinutos = (hhmm) => {
    if (!hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return null;
    const [h, m] = hhmm.split(":").map(Number);
    return h * 60 + m;
};

const diaSemanaIso = () => {
    const d = new Date().getDay();
    return d === 0 ? 7 : d;
};

const estaNoHorario = (cfg) => {
    if (!cfg?.auto_conectar_horario) return false;
    const ini = horarioParaMinutos(cfg.horario_inicio);
    const fim = horarioParaMinutos(cfg.horario_fim);
    if (ini == null || fim == null) return false;
    const dias = Array.isArray(cfg.dias_semana) ? cfg.dias_semana : [];
    if (!dias.includes(diaSemanaIso())) return false;
    const agora = horaAgora();
    if (ini <= fim) return agora >= ini && agora <= fim;
    return agora >= ini || agora <= fim;
};

/**
 * Status em que a chamada ACABOU — em nenhum deles existe chamada na tela.
 *
 * Tem que cobrir tudo que o onEnd() produz. Ficou faltando "perdida" por muito
 * tempo, em três telas: ligação que o cliente desligava antes de ser atendida
 * ficava aparecendo como ativa para sempre. Mexeu no onEnd? Confira aqui.
 */
export const STATUS_FINAIS = ["encerrada", "perdida", "falhou", "recusada", "cancelada"];

export const SoftphoneContext = createContext(null);

export const useSoftphone = () => {
    const ctx = useContext(SoftphoneContext);
    if (!ctx) throw new Error("useSoftphone deve ser usado dentro do SoftphoneProvider");
    return ctx;
};

export const SoftphoneProvider = ({ children }) => {
    const auth = useContext(AuthContext);
    const authenticated = auth?.authenticated;

    const uaRef = useRef(null);
    // Modo PABX próprio: o ramal que RECEBE fica no nosso Asterisk (é ele quem
    // toca em todos, passa pro bot e grava). Enquanto a operadora não fornecer
    // tronco de saída, quem DISCA continua sendo um registro na NVOIP — assim a
    // migração da entrada não leva o discador junto.
    const uaSaidaRef = useRef(null);
    const dialTimeoutRef = useRef(null);
    const progressoRef = useRef(false);
    const sessionRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const audioCtxRef = useRef(null);
    const ringbackRef = useRef(null);
    const ringtoneRef = useRef(null);
    const recorderRef = useRef(null);
    const recordedChunksRef = useRef({ atendente: [], cliente: [] });
    const callDbIdRef = useRef(null);
    const autoConnectTimerRef = useRef(null);
    const heartbeatTimerRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const lastHeartbeatRef = useRef(null);
    const credentialsRef = useRef(null);
    const answerClickRef = useRef(null);
    const userDisconnectingRef = useRef(false);
    const wasRegisteredRef = useRef(false);
    const globalConfigRef = useRef(DEFAULT_GLOBAL_CONFIG);

    const [credentials, setCredentials] = useState(null);
    const [credError, setCredError] = useState(null);
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [globalConfig, setGlobalConfig] = useState(DEFAULT_GLOBAL_CONFIG);
    const [isAdmin, setIsAdmin] = useState(false);
    const [status, setStatus] = useState("desconectado");
    const [registered, setRegistered] = useState(false);
    // null = não há UA de saída (tudo sai pelo ramal principal).
    // false = existe mas não registrou — discar daqui não chega em lugar nenhum.
    const [registeredSaida, setRegisteredSaida] = useState(null);
    const [call, setCall] = useState(null);
    const [activeCliente, setActiveCliente] = useState(null);
    const [error, setError] = useState(null);
    const [heartbeatOk, setHeartbeatOk] = useState(true);
    const [lastHeartbeatAt, setLastHeartbeatAt] = useState(null);
    const [recording, setRecording] = useState(false);
    const [overrideDetected, setOverrideDetected] = useState(false);

    // mantém a ref sempre atualizada — evita closure stale no attachSession
    useEffect(() => { globalConfigRef.current = globalConfig; }, [globalConfig]);
    const [pendingConfirm, setPendingConfirm] = useState(null); // { status, ocupado, provavelmente_este_user }

    const ensureRemoteAudio = useCallback(() => {
        if (!remoteAudioRef.current) {
            const el = document.createElement("audio");
            el.autoplay = true;
            el.setAttribute("playsinline", "true");
            el.style.display = "none";
            document.body.appendChild(el);
            remoteAudioRef.current = el;
        }
        return remoteAudioRef.current;
    }, []);

    const ensureAudioContext = useCallback(() => {
        if (!audioCtxRef.current) {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return null;
            audioCtxRef.current = new Ctx();
        }
        if (audioCtxRef.current.state === "suspended") {
            audioCtxRef.current.resume().catch(() => {});
        }
        return audioCtxRef.current;
    }, []);

    const startRingback = useCallback(() => {
        if (ringbackRef.current) return;
        const ctx = ensureAudioContext();
        if (!ctx) return;
        try {
            const gain = ctx.createGain();
            gain.gain.value = 0;
            gain.connect(ctx.destination);
            const osc = ctx.createOscillator();
            osc.frequency.value = 425;
            osc.connect(gain);
            osc.start();
            const schedule = () => {
                const t = ctx.currentTime;
                gain.gain.cancelScheduledValues(t);
                gain.gain.setValueAtTime(0.15, t);
                gain.gain.setValueAtTime(0, t + 1.0);
            };
            schedule();
            const interval = setInterval(schedule, 5000);
            ringbackRef.current = { osc, gain, interval };
        } catch (_) {}
    }, [ensureAudioContext]);

    const stopRingback = useCallback(() => {
        if (!ringbackRef.current) return;
        try {
            clearInterval(ringbackRef.current.interval);
            ringbackRef.current.gain.gain.cancelScheduledValues(0);
            ringbackRef.current.gain.gain.value = 0;
            ringbackRef.current.osc.stop();
        } catch (_) {}
        ringbackRef.current = null;
    }, []);

    const startRingtone = useCallback(() => {
        if (ringtoneRef.current) return;
        const ctx = ensureAudioContext();
        if (!ctx) return;
        try {
            const gain = ctx.createGain();
            gain.gain.value = 0;
            gain.connect(ctx.destination);
            const osc = ctx.createOscillator();
            osc.frequency.value = 800;
            osc.connect(gain);
            osc.start();
            const volume = Math.max(0, Math.min(1, (config.volume_toque ?? 70) / 100));
            const schedule = () => {
                const t = ctx.currentTime;
                gain.gain.cancelScheduledValues(t);
                for (let k = 0; k < 4; k++) {
                    const ts = t + k * 0.4;
                    gain.gain.setValueAtTime(volume * 0.3, ts);
                    gain.gain.setValueAtTime(0, ts + 0.2);
                }
            };
            schedule();
            const interval = setInterval(schedule, 2000);
            ringtoneRef.current = { osc, gain, interval };
        } catch (_) {}
    }, [ensureAudioContext, config.volume_toque]);

    const stopRingtone = useCallback(() => {
        if (!ringtoneRef.current) return;
        try {
            clearInterval(ringtoneRef.current.interval);
            ringtoneRef.current.gain.gain.cancelScheduledValues(0);
            ringtoneRef.current.gain.gain.value = 0;
            ringtoneRef.current.osc.stop();
        } catch (_) {}
        ringtoneRef.current = null;
    }, []);

    const applyAudioVolume = useCallback(() => {
        if (remoteAudioRef.current) {
            const vol = Math.max(0, Math.min(1, (config.volume_chamada ?? 90) / 100));
            try { remoteAudioRef.current.volume = vol; } catch (_) {}
        }
    }, [config.volume_chamada]);

    const applySinkId = useCallback(async () => {
        if (!remoteAudioRef.current || !config.autofalante_device_id) return;
        if (typeof remoteAudioRef.current.setSinkId !== "function") return;
        try { await remoteAudioRef.current.setSinkId(config.autofalante_device_id); } catch (_) {}
    }, [config.autofalante_device_id]);

    const attachRemoteStream = useCallback((pc) => {
        if (!pc) return;
        const audio = ensureRemoteAudio();
        applyAudioVolume();
        applySinkId();
        const setStream = (stream) => {
            try {
                audio.srcObject = stream;
                const p = audio.play();
                if (p && typeof p.catch === "function") p.catch(() => {});
            } catch (_) {}
        };
        pc.getReceivers?.().forEach((r) => {
            if (r.track && r.track.kind === "audio") {
                const s = new MediaStream();
                s.addTrack(r.track);
                setStream(s);
            }
        });
        pc.addEventListener("track", (ev) => {
            if (ev.streams && ev.streams[0]) setStream(ev.streams[0]);
            else if (ev.track) {
                const s = new MediaStream();
                s.addTrack(ev.track);
                setStream(s);
            }
        });
        if ("onaddstream" in pc) {
            pc.onaddstream = (ev) => ev.stream && setStream(ev.stream);
        }
    }, [ensureRemoteAudio, applyAudioVolume, applySinkId]);

    const startRecorder = useCallback((pc, direction) => {
        // No PABX próprio quem grava a entrada é o servidor (MixMonitor), e grava
        // sempre — inclusive quando ninguém está com a aba aberta. Gravar de novo
        // aqui só duplicaria arquivo e pediria microfone à toa. A saída não passa
        // pelo nosso PABX: se o navegador não gravar, ninguém grava.
        if (!gravaNoNavegador(credentialsRef.current, direction)) {
            console.debug("[softphone] gravacao no navegador desligada: quem grava e o PABX");
            return;
        }
        if (!pc || recorderRef.current) {
            console.debug("[softphone] startRecorder ignorado: pc=" + !!pc + " jaRodando=" + !!recorderRef.current);
            return;
        }
        if (typeof window.MediaRecorder === "undefined") {
            console.warn("[softphone] MediaRecorder nao suportado");
            return;
        }
        try {
            const mimeCandidates = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];
            const mimeType = mimeCandidates.find((m) => window.MediaRecorder.isTypeSupported?.(m)) || "";
            const opts = mimeType ? { mimeType, audioBitsPerSecond: 64000 } : undefined;

            recordedChunksRef.current = { atendente: [], cliente: [] };

            // ---- Atendente: usa diretamente o sender track (microfone local) ----
            const senderAudio = (pc.getSenders?.() || []).find((s) => s.track?.kind === "audio");
            let recAtendente = null;
            if (senderAudio?.track) {
                const streamA = new MediaStream([senderAudio.track]);
                recAtendente = new MediaRecorder(streamA, opts);
                recAtendente.ondataavailable = (e) => { if (e.data?.size > 0) recordedChunksRef.current.atendente.push(e.data); };
                recAtendente.onerror = (e) => console.error("[softphone] rec atendente onerror:", e);
                recAtendente.start(1000);
                console.debug("[softphone] rec atendente iniciado (track " + senderAudio.track.id + ")");
            } else {
                console.warn("[softphone] sender audio nao encontrado — atendente nao sera gravado ainda");
            }

            // ---- Cliente: aguarda primeiro receiver de áudio ----
            // (MediaRecorder trava a lista de tracks no .start(), então precisamos
            // do track ANTES de iniciar — não dá pra addTrack depois.)
            let recCliente = null;
            const tryStartCliente = (track, origem) => {
                if (recCliente) return;
                if (!track || track.kind !== "audio") return;
                const streamC = new MediaStream([track]);
                recCliente = new MediaRecorder(streamC, opts);
                recCliente.ondataavailable = (e) => { if (e.data?.size > 0) recordedChunksRef.current.cliente.push(e.data); };
                recCliente.onerror = (e) => console.error("[softphone] rec cliente onerror:", e);
                recCliente.start(1000);
                if (recorderRef.current) recorderRef.current.recCliente = recCliente;
                console.debug("[softphone] rec cliente iniciado (" + origem + " track " + track.id + ")");
            };

            // Tenta com receiver atual
            const receiverAudio = (pc.getReceivers?.() || []).find((r) => r.track?.kind === "audio");
            if (receiverAudio?.track) {
                tryStartCliente(receiverAudio.track, "receiver");
            }

            // Tenta também via track event (caso o receiver chegue só depois)
            const onTrack = (ev) => tryStartCliente(ev.track, "ontrack");
            pc.addEventListener("track", onTrack);

            recorderRef.current = { recAtendente, recCliente, mimeType, pc, onTrack };
            setRecording(true);
            console.debug("[softphone] recorders duplos preparados (mime=" + mimeType + ")");
        } catch (e) {
            console.error("[softphone] erro iniciando recorder:", e);
            recorderRef.current = null;
            setRecording(false);
        }
    }, []);

    const stopRecorder = useCallback(async () => {
        const r = recorderRef.current;
        if (!r) return null;
        const mime = r.mimeType || "audio/webm";
        const cleanup = () => {
            try { if (r.pc && r.onTrack) r.pc.removeEventListener("track", r.onTrack); } catch (_) {}
        };
        const stopOne = (rec, label) => new Promise((resolve) => {
            try {
                if (!rec || rec.state === "inactive") return resolve(null);
                rec.onstop = () => {
                    const chunks = label === "atendente" ? recordedChunksRef.current.atendente : recordedChunksRef.current.cliente;
                    const blob = new Blob(chunks, { type: mime });
                    console.debug("[softphone] " + label + " parado: " + blob.size + "B em " + chunks.length + " chunks");
                    resolve(blob);
                };
                rec.stop();
            } catch (e) {
                console.error("[softphone] erro parando " + label + ":", e);
                resolve(null);
            }
        });
        const [blobAtendente, blobCliente] = await Promise.all([
            stopOne(r.recAtendente, "atendente"),
            stopOne(r.recCliente, "cliente"),
        ]);
        cleanup();
        recordedChunksRef.current = { atendente: [], cliente: [] };
        recorderRef.current = null;
        setRecording(false);
        if ((!blobAtendente || blobAtendente.size === 0) && (!blobCliente || blobCliente.size === 0)) return null;
        return { atendente: blobAtendente, cliente: blobCliente };
    }, []);

    const triggerNotification = useCallback((numero) => {
        if (!config.notificacao_browser) return;
        try {
            if (typeof Notification !== "undefined" && Notification.permission === "granted") {
                new Notification("Chamada recebida", { body: numero, icon: "/favicon.ico" });
            }
        } catch (_) {}
    }, [config.notificacao_browser]);

    const attachSession = useCallback((session, direction, overrideRemote = null) => {
        sessionRef.current = session;

        // refs locais por sessão — sobrevivem ao closure quando 'ended'/'failed' dispara
        const sessionState = {
            answeredAt: null,
            startedAt: new Date(),
        };

        const initial = {
            direction,
            remote: overrideRemote || session.remote_identity?.uri?.user || "",
            displayName: session.remote_identity?.display_name || "",
            status: direction === "incoming" ? "tocando" : "discando",
            startedAt: sessionState.startedAt,
            answeredAt: null,
            endedAt: null,
            durationSec: 0,
            muted: false,
            onHold: false,
            callId: session.id,
        };
        setCall(initial);

        if (session.connection) attachRemoteStream(session.connection);
        session.on("peerconnection", (e) => attachRemoteStream(e.peerconnection));

        // O JsSIP só manda o INVITE (ou o 200 OK, ao atender) depois que a coleta
        // de ICE termina. "Terminar" para ele é o navegador emitir o candidato
        // nulo ou iceGatheringState virar 'complete' — e há rede em que isso
        // nunca acontece: fica em 'gathering' para sempre e a chamada não sai,
        // sem erro nenhum. Foi o que travou a discagem em 16/09.
        //
        // O próprio JsSIP oferece a saída: o evento 'icecandidate' entrega um
        // ready() que libera a espera. Soltamos pouco depois do primeiro
        // candidato — o que já foi coletado basta, e o resto do ICE continua
        // por trickle. Se a coleta terminar sozinha antes, o ready() daqui não
        // faz nada (o JsSIP ignora a segunda chamada).
        let liberarIce = null;
        let iceTimer = null;
        const soltarIce = () => {
            if (!liberarIce) return;
            const fn = liberarIce;
            liberarIce = null;
            try { fn(); } catch (_) {}
        };
        session.on("icecandidate", ({ candidate, ready }) => {
            liberarIce = ready;
            if (!iceTimer) {
                iceTimer = setTimeout(() => {
                    iceTimer = null;
                    console.debug("[softphone] liberando SDP sem esperar o fim do ICE");
                    soltarIce();
                }, ICE_ESPERA_MS);
            }
            if (candidate?.candidate?.includes(" srflx ")) {
                console.debug("[softphone] candidato público obtido");
            }
        });
        const pararIceTimer = () => { if (iceTimer) { clearTimeout(iceTimer); iceTimer = null; } liberarIce = null; };

        session.on("progress", () => {
            setCall((c) => c ? { ...c, status: direction === "incoming" ? "tocando" : "chamando" } : c);
            if (direction === "outgoing") { progressoRef.current = true; startRingback(); }
        });

        const onAnswered = () => {
            if (!sessionState.answeredAt) sessionState.answeredAt = new Date();
            setCall((c) => c ? { ...c, status: "em_andamento", answeredAt: c.answeredAt || sessionState.answeredAt } : c);
            stopRingback();
            stopRingtone();
            if (session.connection) {
                attachRemoteStream(session.connection);
                const gc = globalConfigRef.current;
                console.debug("[softphone] onAnswered - globalConfig:", gc);
                if (gc.transcrever_auto && gc.aceitou_termos_gravacao) {
                    console.debug("[softphone] iniciando recorder automaticamente");
                    startRecorder(session.connection, direction);
                } else {
                    console.debug("[softphone] recorder NAO iniciado (transcrever_auto=" + gc.transcrever_auto + " aceitou_termos=" + gc.aceitou_termos_gravacao + ")");
                }
            }
            if (callDbIdRef.current) {
                atualizarSoftphoneChamada(callDbIdRef.current, {
                    status: "atendida",
                    atendida_em: sessionState.answeredAt.toISOString(),
                });
            }
        };
        session.on("accepted", onAnswered);
        session.on("confirmed", onAnswered);

        const onEnd = async (isFail, cause) => {
            const endedAt = new Date();
            const wasAnswered = !!sessionState.answeredAt;

            // status final no BD (regra clara, independente do isFail do JsSIP)
            let statusFinal;
            if (wasAnswered) {
                statusFinal = "encerrada";
            } else if (direction === "incoming") {
                // recebida e não atendida -> perdida; se foi rejeitada por nós, usa "recusada"
                statusFinal = (cause === "Rejected" || cause === "Busy") ? "recusada" : "perdida";
            } else {
                // saída não atendida -> cancelada
                statusFinal = "cancelada";
            }

            setCall((c) => {
                if (!c) return c;
                return {
                    ...c,
                    status: statusFinal === "encerrada" ? "encerrada" : (statusFinal === "perdida" ? "perdida" : "falhou"),
                    endedAt,
                    failureCause: cause || c.failureCause,
                };
            });
            stopRingback();
            stopRingtone();

            const dbId = callDbIdRef.current;
            const blob = await stopRecorder();
            sessionRef.current = null;

            // Diagnóstico: persiste a causa SIP e se o atendente chegou a clicar em
            // Atender. Sem isso não dá pra distinguir "cliente desligou" de "falha
            // de mídia/rede ao atender" (relato de chamada que cai sozinha).
            const clicouEm = answerClickRef.current;
            answerClickRef.current = null;
            let obs = null;
            if (isFail || !wasAnswered) {
                obs = "causa_sip=" + (cause || "fim_normal");
                if (direction === "incoming") {
                    obs += clicouEm
                        ? "; clicou_atender_ha_" + Math.max(0, Math.round((endedAt - clicouEm) / 1000)) + "s"
                        : "; sem_clique_atender";
                }
            }

            if (dbId) {
                const duracao = wasAnswered
                    ? Math.max(0, Math.floor((endedAt - sessionState.answeredAt) / 1000))
                    : 0;
                const payload = {
                    encerrada_em: endedAt.toISOString(),
                    status: statusFinal,
                    duracao_seg: duracao,
                    ...(obs ? { observacoes: obs } : {}),
                };
                atualizarSoftphoneChamada(dbId, payload);
                // Se houve gravação (manual ou automática), SEMPRE envia o blob
                // pro backend persistir. O backend salva o arquivo + transcreve.
                const totalBytes = blob ? ((blob.atendente?.size || 0) + (blob.cliente?.size || 0)) : 0;
                console.debug("[softphone] onEnd - wasAnswered=" + wasAnswered + " blob=" + totalBytes + "B dbId=" + dbId);
                if (wasAnswered && blob && totalBytes > 0) {
                    console.debug("[softphone] enviando gravacao pro backend...");
                    transcreverSoftphoneChamada(dbId, blob).then((r) => {
                        if (r?.data?.transcricao) console.debug("[softphone] transcricao OK:", r.data.transcricao.slice(0, 120));
                        else if (r?.error) console.warn("[softphone] erro ao transcrever/salvar:", r.error, r.detalhes);
                        else console.debug("[softphone] upload concluido sem texto:", r);
                    }).catch((e) => console.error("[softphone] excecao no upload:", e));
                }
                callDbIdRef.current = null;
            }
        };

        const limparDialTimeout = () => { pararIceTimer(); if (dialTimeoutRef.current) { clearTimeout(dialTimeoutRef.current); dialTimeoutRef.current = null; } };
        session.on("accepted", () => { progressoRef.current = true; limparDialTimeout(); });
        session.on("ended", () => { limparDialTimeout(); onEnd(false); });
        session.on("failed", (e) => { limparDialTimeout(); onEnd(true, e?.cause); });

        if (direction === "outgoing") {
            startRingback();
            // Discagem sem NENHUMA resposta ficava tocando pra sempre: o toque é
            // gerado aqui, não vem da operadora. Sem este corte o atendente ouve
            // "chamando" indefinidamente e não sobra nada para investigar.
            progressoRef.current = false;
            if (dialTimeoutRef.current) clearTimeout(dialTimeoutRef.current);
            dialTimeoutRef.current = setTimeout(() => {
                if (progressoRef.current || sessionRef.current !== session) return;
                const pc = session.connection;
                const ice = pc?.iceGatheringState || "sem-pc";
                const sinal = pc?.signalingState || "-";
                const etapa = ice !== "complete"
                    ? `o navegador não terminou de preparar o áudio (ICE ${ice}) — o convite nem saiu`
                    : `a operadora não respondeu ao convite (sinalização ${sinal})`;
                console.warn("[softphone] discagem sem resposta:", { ice, sinal });
                setError(`Chamada sem resposta em 20s: ${etapa}.`);
                try { session.terminate(); } catch (_) {}
            }, 20000);
        }

        if (direction === "incoming") {
            startRingtone();
            triggerNotification(initial.remote);
            if (config.auto_atender) {
                const delayMs = Math.max(0, (config.auto_atender_apos_seg ?? 0)) * 1000;
                setTimeout(() => {
                    if (sessionRef.current === session && session.direction === "incoming") {
                        try {
                            session.answer({ mediaConstraints: { audio: true, video: false }, pcConfig: PC_CONFIG });
                        } catch (_) {}
                    }
                }, delayMs);
            }
        }
    }, [attachRemoteStream, startRingback, stopRingback, startRingtone, stopRingtone, startRecorder, stopRecorder, triggerNotification, config.auto_atender, config.auto_atender_apos_seg]);

    const connect = useCallback(async (force = false) => {
        if (uaRef.current) return;
        const creds = credentialsRef.current;
        if (!creds?.numbersip || !creds?.senha || !creds?.wss_uri || !creds?.sip_domain) {
            setError("Credenciais SIP incompletas");
            return;
        }

        // Feedback imediato: o clique não pode parecer "morto" enquanto o pré-check roda.
        setError(null);
        setStatus("conectando");

        // Pré-check (cortesia): avisa se o ramal já está Online em outro lugar. NÃO pode
        // travar a conexão — a API da NVOIP às vezes demora/timeouta (até 15s); por isso
        // damos no máximo ~4s e seguimos assim mesmo.
        if (!force) {
            let r = null;
            try {
                r = await Promise.race([
                    getSoftphoneRamalStatus(),
                    new Promise((resolve) => setTimeout(() => resolve(null), 4000)),
                ]);
            } catch (_) { /* pré-check é best-effort */ }
            if (r?.data?.ocupado && !r.data.provavelmente_este_user) {
                setStatus("desconectado");
                setPendingConfirm(r.data);
                return; // não conecta agora — frontend mostra modal e chama connect(true)
            }
        }

        ensureAudioContext();
        userDisconnectingRef.current = false;
        setOverrideDetected(false);
        setPendingConfirm(null);

        try {
            const socket = new JsSIP.WebSocketInterface(creds.wss_uri);
            const ua = new JsSIP.UA({
                sockets: [socket],
                uri: `sip:${creds.numbersip}@${creds.sip_domain}`,
                password: creds.senha,
                register: true,
                session_timers: false,
                user_agent: "TagaWebphone/2.0",
                connection_recovery_min_interval: 2,
                connection_recovery_max_interval: 30,
                // Ping a cada 30s. O caminho do ramal passa por Cloudflare e
                // Apache, e WebSocket parado nos dois é WebSocket derrubado —
                // e derrubar no meio do toque significa o atendente clicar em
                // atender e nada acontecer. O worker do bot já fazia isso.
                keep_alive_interval: 30,
                register_expires: 300,
            });

            // Handlers presos a ESTE UA. Desconectar e conectar de novo deixa o
            // UA antigo ainda emitindo disconnected/unregistered enquanto fecha,
            // e esses eventos zeravam o estado do UA novo: a tela ficava
            // "Offline" com o ramal registrado e o botão Conectar inerte
            // ("if (uaRef.current) return"), só saindo com F5. Mesmo guard do
            // worker do bot (node_workers/bot_ligacao/src/sip-ua.js).
            uaRef.current = ua;
            const atual = () => uaRef.current === ua;

            // Watchdog: se não registrar em 15s (ex.: WSS travado na rede), avisa e limpa o
            // UA — senão o próximo clique cai em "if (uaRef.current) return" e nada acontece.
            let settled = false;
            const watchdog = setTimeout(() => {
                if (settled || userDisconnectingRef.current || !atual()) return;
                settled = true;
                setError("Não foi possível conectar ao servidor de voz (tempo esgotado). Verifique a conexão e tente novamente.");
                try { ua.stop(); } catch (_) {}
                uaRef.current = null;
                setStatus("desconectado");
                setRegistered(false);
            }, 15000);

            ua.on("connecting", () => { if (atual()) setStatus("conectando"); });
            ua.on("connected", () => { if (atual()) setStatus("conectado"); });
            ua.on("disconnected", () => {
                if (!atual()) return;
                setStatus("desconectado");
                setRegistered(false);
                // Se o user NÃO pediu desconexão e estava registrado, é override / queda
                if (!userDisconnectingRef.current && wasRegisteredRef.current) {
                    setOverrideDetected(true);
                }
                wasRegisteredRef.current = false;
            });
            ua.on("registered", () => {
                if (!atual()) return;
                settled = true;
                clearTimeout(watchdog);
                setStatus("registrado");
                setRegistered(true);
                wasRegisteredRef.current = true;
                userDisconnectingRef.current = false;
                setOverrideDetected(false);
                setError(null);
            });
            ua.on("unregistered", () => {
                if (!atual()) return;
                setRegistered(false);
                if (!userDisconnectingRef.current && wasRegisteredRef.current) {
                    setOverrideDetected(true);
                    setError("Conexão SIP perdida — outro dispositivo pode ter conectado neste ramal.");
                }
            });
            ua.on("registrationFailed", (e) => {
                if (!atual()) return;
                settled = true;
                clearTimeout(watchdog);
                setRegistered(false);
                setError(`Falha no registro: ${e?.cause || "desconhecido"}`);
            });

            ua.on("newRTCSession", (data) => {
                if (!atual()) return;
                const { session, originator, request } = data;
                if (originator === "remote") {
                    // Ramal já em uma chamada viva? Rejeita a nova com 486 (ocupado) e
                    // mantém a atual intacta. Sem isso, attachSession sobrescreveria a
                    // sessão ativa (sessionRef) e derrubaria a chamada em andamento.
                    if (sessionRef.current && !sessionRef.current.isEnded()) {
                        try { session.terminate({ status_code: 486, reason_phrase: "Busy Here" }); } catch (_) {}
                        console.debug("[softphone] 2a chamada rejeitada: ramal ocupado");
                        return;
                    }

                    const remoteNum = extractCallerNumber(request, creds.numbersip);

                    // Headers que ajudam a debugar de onde veio a chamada (NVOIP varia)
                    if (typeof window !== "undefined" && window.console) {
                        try {
                            const debugHeaders = {};
                            ["From", "To", "Contact", "P-CALLERID", "P-Asserted-Identity",
                             "Remote-Party-ID", "X-Caller-Id", "X-Caller-Number",
                             "X-Original-Calling-Number", "Diversion", "Call-ID"].forEach((h) => {
                                try { debugHeaders[h] = request.getHeader?.(h); } catch (_) {}
                            });
                            console.debug("[softphone] incoming call headers:", {
                                ...debugHeaders,
                                "From.display_name": request?.from?.display_name,
                                bina_resolvida: remoteNum,
                            });
                        } catch (_) {}
                    }

                    // Anexa listeners IMEDIATAMENTE — antes de qualquer await — pra
                    // não perder eventos progress/failed enquanto a Promise pende.
                    attachSession(session, "incoming", remoteNum);

                    // Em paralelo: lookup do cliente e registro no BD.
                    lookupSoftphoneCliente(remoteNum).then((lookup) => {
                        setActiveCliente(lookup?.data || null);
                    }).catch(() => {});

                    // No modo PBX a linha da ligação já nasceu no servidor
                    // (PbxService::rotaEntrada, origem=pbx) quando o INVITE
                    // chegou. Como o PABX toca em todos os ramais ao mesmo
                    // tempo, cada navegador criava mais uma cópia da MESMA
                    // ligação — e, quando o bot ou o colega atendia primeiro, o
                    // CANCEL fechava essa cópia como "perdida". Aqui só tocamos
                    // a campainha; quem registra é o PABX.
                    if (creds.modo === "pbx") {
                        callDbIdRef.current = null;
                    } else {
                        registrarSoftphoneChamada({
                            direcao: "entrada",
                            numero: remoteNum,
                            call_id_sip: session.id,
                            status: "tocando",
                        }).then((reg) => {
                            callDbIdRef.current = reg?.data?.id || null;
                        }).catch(() => {});
                    }
                } else {
                    attachSession(session, "outgoing");
                }
            });

            ua.start();

            // UA só de saída (NVOIP), quando o backend mandar `saida`. Não escuta
            // chamada: o DID já aponta para o nosso PABX, então nada entra por aqui.
            if (creds.saida?.numbersip && creds.saida?.senha && creds.saida?.wss_uri) {
                try {
                    const uaOut = new JsSIP.UA({
                        sockets: [new JsSIP.WebSocketInterface(creds.saida.wss_uri)],
                        uri: `sip:${creds.saida.numbersip}@${creds.saida.sip_domain}`,
                        password: creds.saida.senha,
                        register: true,
                        session_timers: false,
                        user_agent: "TagaWebphone/2.0-saida",
                        connection_recovery_min_interval: 2,
                        connection_recovery_max_interval: 30,
                        keep_alive_interval: 30,
                    });
                    // Mesmo guard do UA de entrada: o UA de saída antigo não pode
                    // marcar o novo como não registrado enquanto termina de fechar.
                    uaSaidaRef.current = uaOut;
                    setRegisteredSaida(false); // vira true quando a operadora aceitar
                    const atualSaida = () => uaSaidaRef.current === uaOut;

                    uaOut.on("registered", () => {
                        if (!atualSaida()) return;
                        console.debug("[softphone] saida registrada:", creds.saida.numbersip);
                        setRegisteredSaida(true);
                    });
                    uaOut.on("unregistered", () => { if (atualSaida()) setRegisteredSaida(false); });
                    uaOut.on("disconnected", () => { if (atualSaida()) setRegisteredSaida(false); });
                    uaOut.on("registrationFailed", (e) => {
                        if (!atualSaida()) return;
                        console.warn("[softphone] registro de saida falhou:", e?.cause);
                        setRegisteredSaida(false);
                    });
                    uaOut.on("newRTCSession", (data) => {
                        if (!atualSaida()) return;
                        if (data.originator === "remote") {
                            // Entrada é com o PABX. Recusa sem derrubar a chamada em curso.
                            try { data.session.terminate({ status_code: 486, reason_phrase: "Busy Here" }); } catch (_) {}
                            return;
                        }
                        attachSession(data.session, "outgoing");
                    });
                    uaOut.start();
                } catch (e) {
                    console.warn("[softphone] nao subiu o UA de saida:", e?.message || e);
                    setRegisteredSaida(false);
                }
            }
        } catch (e) {
            uaRef.current = null;
            setStatus("desconectado");
            setError(`Erro ao iniciar SIP: ${e.message || e}`);
        }
    }, [attachSession, ensureAudioContext]);

    const disconnect = useCallback(() => {
        userDisconnectingRef.current = true;
        try {
            if (sessionRef.current) { try { sessionRef.current.terminate(); } catch (_) {} sessionRef.current = null; }
            if (uaRef.current) { uaRef.current.stop(); uaRef.current = null; }
            if (uaSaidaRef.current) { try { uaSaidaRef.current.stop(); } catch (_) {} uaSaidaRef.current = null; }
            setRegisteredSaida(null);
        } finally {
            stopRingback();
            stopRingtone();
            setStatus("desconectado");
            setRegistered(false);
            setCall(null);
            setActiveCliente(null);
            setOverrideDetected(false);
        }
    }, [stopRingback, stopRingtone]);

    const dial = useCallback(async (numero) => {
        if (!uaRef.current || !registered) {
            setError("Não está registrado. Conecte primeiro.");
            return;
        }
        const target = normalizeNumber(numero);
        if (!target) {
            setError("Número inválido");
            return;
        }
        ensureAudioContext();

        // Sai pelo UA de saída quando ele existe; senão, pelo próprio ramal.
        // Se ele existe e não registrou, a discagem não chega a lugar nenhum e o
        // atendente só ouve silêncio — melhor falhar com aviso do que fingir.
        // Vem ANTES de abrir a linha no banco: registrar primeiro deixava uma
        // chamada "discando" órfã para sempre a cada erro daqui.
        if (uaSaidaRef.current && registeredSaida === false) {
            setError("Linha de saída (operadora) não está conectada. Desconecte e conecte o softphone; se insistir, avise o suporte.");
            return;
        }
        const uaOut = uaSaidaRef.current || uaRef.current;
        const domain = uaOut.configuration.uri.host;
        const audioCons = config.microfone_device_id
            ? { audio: { deviceId: { ideal: config.microfone_device_id } }, video: false }
            : { audio: true, video: false };

        const lookup = await lookupSoftphoneCliente(target);
        setActiveCliente(lookup?.data || null);

        const reg = await registrarSoftphoneChamada({
            direcao: "saida",
            numero: target,
            status: "discando",
        });
        callDbIdRef.current = reg?.data?.id || null;

        try {
            uaOut.call(`sip:${target}@${domain}`, {
                mediaConstraints: audioCons,
                pcConfig: PC_CONFIG,
                rtcOfferConstraints: { offerToReceiveAudio: 1, offerToReceiveVideo: 0 },
            });
        } catch (e) {
            // A sessão nem chegou a nascer, então não há 'failed' para fechar a
            // linha: fecha aqui, senão ela fica "discando" para sempre.
            const dbId = callDbIdRef.current;
            callDbIdRef.current = null;
            if (dbId) {
                atualizarSoftphoneChamada(dbId, {
                    status: "falhou",
                    encerrada_em: new Date().toISOString(),
                    observacoes: "causa_sip=erro_ao_discar",
                });
            }
            setError(`Não foi possível iniciar a chamada: ${e?.message || e}`);
        }
    }, [registered, registeredSaida, ensureAudioContext, config.microfone_device_id]);

    const answer = useCallback(() => {
        if (sessionRef.current && sessionRef.current.direction === "incoming") {
            ensureAudioContext();
            // "ideal" (não "exact"): se o microfone salvo sumiu (headset BT dormindo,
            // USB re-enumerada), o navegador cai pro mic padrão em vez de falhar o
            // getUserMedia — com "exact" o JsSIP respondia 480 e derrubava a chamada
            // no exato momento do clique em Atender.
            const audioCons = config.microfone_device_id
                ? { audio: { deviceId: { ideal: config.microfone_device_id } }, video: false }
                : { audio: true, video: false };
            answerClickRef.current = new Date();
            try {
                sessionRef.current.answer({ mediaConstraints: audioCons, pcConfig: PC_CONFIG });
            } catch (e) {
                // remoto pode ter cancelado no exato momento do clique
                console.warn("[softphone] answer() falhou:", e?.message || e);
            }
            stopRingtone();
        }
    }, [stopRingtone, ensureAudioContext, config.microfone_device_id]);

    const hangup = useCallback(() => {
        if (sessionRef.current) {
            try { sessionRef.current.terminate(); } catch (_) {}
            sessionRef.current = null;
        }
        stopRingback();
        stopRingtone();
    }, [stopRingback, stopRingtone]);

    const toggleMute = useCallback(() => {
        if (!sessionRef.current) return;
        const s = sessionRef.current;
        const muted = s.isMuted().audio;
        if (muted) s.unmute({ audio: true }); else s.mute({ audio: true });
        setCall((c) => c ? { ...c, muted: !muted } : c);
    }, []);

    const toggleHold = useCallback(() => {
        if (!sessionRef.current) return;
        const s = sessionRef.current;
        const held = s.isOnHold().local;
        if (held) s.unhold(); else s.hold();
        setCall((c) => c ? { ...c, onHold: !held } : c);
    }, []);

    const sendDtmf = useCallback((tone) => {
        if (sessionRef.current) {
            try { sessionRef.current.sendDTMF(String(tone)); } catch (_) {}
        }
    }, []);

    const transfer = useCallback((numero) => {
        if (!sessionRef.current) return;
        const target = normalizeNumber(numero);
        if (!target) return;
        const domain = uaRef.current?.configuration.uri.host || "";
        try { sessionRef.current.refer(`sip:${target}@${domain}`); } catch (_) {}
    }, []);

    const startRecordingManual = useCallback(() => {
        if (!sessionRef.current || !sessionRef.current.connection) return;
        if (!globalConfig.aceitou_termos_gravacao) {
            setError("Os termos de gravação ainda não foram aceitos globalmente. Peça ao admin pra liberar nas configurações.");
            return;
        }
        startRecorder(sessionRef.current.connection, sessionRef.current.direction);
    }, [startRecorder, globalConfig.aceitou_termos_gravacao]);

    const stopRecordingManual = useCallback(async () => {
        const blob = await stopRecorder();
        const dbId = callDbIdRef.current;
        const totalBytes = blob ? ((blob.atendente?.size || 0) + (blob.cliente?.size || 0)) : 0;
        if (blob && totalBytes > 0 && dbId) {
            return transcreverSoftphoneChamada(dbId, blob);
        }
        return null;
    }, [stopRecorder]);

    const transcreverChamada = useCallback(async (id) => {
        return transcreverSoftphoneChamada(id);
    }, []);

    const saveConfig = useCallback(async (novo) => {
        const merged = { ...config, ...novo };
        setConfig(merged);
        const res = await apiSaveConfig(novo);
        if (res?.data) setConfig({ ...DEFAULT_CONFIG, ...res.data });
        return res;
    }, [config]);

    const saveGlobalConfig = useCallback(async (novo) => {
        const res = await apiSaveGlobalConfig(novo);
        if (res?.data) {
            setGlobalConfig({
                transcrever_auto: !!res.data.transcrever_auto,
                idioma_transcricao: res.data.idioma_transcricao,
                aceitou_termos_gravacao: !!res.data.aceitou_termos_gravacao,
            });
        }
        return res;
    }, []);

    const cancelPendingConfirm = useCallback(() => setPendingConfirm(null), []);
    const dismissOverride = useCallback(() => setOverrideDetected(false), []);

    const refreshGlobalConfig = useCallback(async () => {
        const c = await getSoftphoneGlobalConfig();
        if (c?.data) {
            setGlobalConfig({
                transcrever_auto: !!c.data.transcrever_auto,
                idioma_transcricao: c.data.idioma_transcricao,
                aceitou_termos_gravacao: !!c.data.aceitou_termos_gravacao,
            });
            if (typeof c.data.is_admin === "boolean") setIsAdmin(c.data.is_admin);
        }
    }, []);

    // Loader: credenciais + config + global config
    useEffect(() => {
        if (!authenticated) {
            setCredentials(null);
            setCredError(null);
            setConfig(DEFAULT_CONFIG);
            setGlobalConfig(DEFAULT_GLOBAL_CONFIG);
            setIsAdmin(false);
            credentialsRef.current = null;
            return;
        }
        (async () => {
            const r = await getSoftphoneCredentials();
            if (r?.data) {
                setCredentials(r.data);
                credentialsRef.current = r.data;
                setCredError(null);
                if (r.data.config) setConfig({ ...DEFAULT_CONFIG, ...r.data.config });
                if (r.data.global_config) {
                    setGlobalConfig({
                        transcrever_auto: !!r.data.global_config.transcrever_auto,
                        idioma_transcricao: r.data.global_config.idioma_transcricao,
                        aceitou_termos_gravacao: !!r.data.global_config.aceitou_termos_gravacao,
                    });
                }
                if (typeof r.data.is_admin === "boolean") setIsAdmin(r.data.is_admin);
            } else {
                setCredError(r?.error || "Sem credenciais SIP");
                setCredentials(null);
                credentialsRef.current = null;
                // Mesmo sem credenciais SIP, tenta puxar global config (pra admin que vê outros)
                const g = await getSoftphoneGlobalConfig();
                if (g?.data) {
                    setGlobalConfig({
                        transcrever_auto: !!g.data.transcrever_auto,
                        idioma_transcricao: g.data.idioma_transcricao,
                        aceitou_termos_gravacao: !!g.data.aceitou_termos_gravacao,
                    });
                    if (typeof g.data.is_admin === "boolean") setIsAdmin(g.data.is_admin);
                }
            }
        })();
        (async () => {
            const c = await getSoftphoneConfig();
            if (c?.data) setConfig({ ...DEFAULT_CONFIG, ...c.data });
        })();
    }, [authenticated]);

    // Notification permission
    useEffect(() => {
        if (!authenticated) return;
        if (typeof Notification !== "undefined" && Notification.permission === "default") {
            try { Notification.requestPermission(); } catch (_) {}
        }
    }, [authenticated]);

    // Auto-conectar ao abrir
    useEffect(() => {
        if (!authenticated || !credentials) return;
        if (!config.auto_conectar_ao_abrir) return;
        if (uaRef.current) return;
        const t = setTimeout(() => connect(), 800);
        return () => clearTimeout(t);
    }, [authenticated, credentials, config.auto_conectar_ao_abrir, connect]);

    // Auto-conectar por horário
    useEffect(() => {
        if (!authenticated || !credentials) return;
        if (autoConnectTimerRef.current) clearInterval(autoConnectTimerRef.current);
        autoConnectTimerRef.current = setInterval(() => {
            const deveria = estaNoHorario(config);
            const isOn = !!uaRef.current && registered;
            if (deveria && !uaRef.current) connect();
            else if (!deveria && config.auto_conectar_horario && isOn) disconnect();
        }, 30000);
        return () => clearInterval(autoConnectTimerRef.current);
    }, [authenticated, credentials, config, registered, connect, disconnect]);

    // Heartbeat
    useEffect(() => {
        if (!authenticated || !registered) {
            if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
            heartbeatTimerRef.current = null;
            return;
        }
        const interval = (config.heartbeat_seg ?? 15) * 1000;
        const tick = async () => {
            const ok = !!uaRef.current && uaRef.current.isConnected() && uaRef.current.isRegistered();
            const r = await heartbeatSoftphone();
            const success = ok && !!r;
            setHeartbeatOk(success);
            if (success) {
                lastHeartbeatRef.current = new Date();
                setLastHeartbeatAt(lastHeartbeatRef.current);
            }
            if (!ok && config.tentar_reconectar && uaRef.current) {
                try { uaRef.current.register(); } catch (_) {}
            }
        };
        tick();
        heartbeatTimerRef.current = setInterval(tick, interval);
        return () => clearInterval(heartbeatTimerRef.current);
    }, [authenticated, registered, config.heartbeat_seg, config.tentar_reconectar]);

    // Cronômetro da chamada
    useEffect(() => {
        const tick = setInterval(() => {
            setCall((c) => {
                if (!c || !c.answeredAt || c.status !== "em_andamento") return c;
                return { ...c, durationSec: Math.floor((Date.now() - new Date(c.answeredAt).getTime()) / 1000) };
            });
        }, 1000);
        return () => clearInterval(tick);
    }, []);

    // Avisa antes de fechar/recarregar a aba se houver chamada ativa.
    // Limitação técnica: WebRTC + JsSIP são destruídos no reload (não há como
    // preservar a chamada). O melhor é avisar o usuário antes da ação.
    useEffect(() => {
        const inCall = !!call && !STATUS_FINAIS.includes(call.status);
        if (!inCall) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = "Você está em uma chamada. Se sair, ela será encerrada.";
            return e.returnValue;
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [call?.status, call]);

    // Aplicar volume sempre que mudar config
    useEffect(() => { applyAudioVolume(); }, [applyAudioVolume]);
    useEffect(() => { applySinkId(); }, [applySinkId]);

    // Logout = desconecta
    useEffect(() => {
        if (!authenticated) {
            try { if (sessionRef.current) sessionRef.current.terminate(); } catch (_) {}
            try { if (uaRef.current) uaRef.current.stop(); } catch (_) {}
            try { if (uaSaidaRef.current) uaSaidaRef.current.stop(); } catch (_) {}
            sessionRef.current = null;
            uaRef.current = null;
            stopRingback();
            stopRingtone();
            setStatus("desconectado");
            setRegistered(false);
            setCall(null);
            setActiveCliente(null);
        }
    }, [authenticated, stopRingback, stopRingtone]);

    // Cleanup
    useEffect(() => {
        return () => {
            try { if (sessionRef.current) sessionRef.current.terminate(); } catch (_) {}
            try { if (uaRef.current) uaRef.current.stop(); } catch (_) {}
            if (remoteAudioRef.current) {
                try { remoteAudioRef.current.remove(); } catch (_) {}
                remoteAudioRef.current = null;
            }
            if (audioCtxRef.current) {
                try { audioCtxRef.current.close(); } catch (_) {}
                audioCtxRef.current = null;
            }
            if (autoConnectTimerRef.current) clearInterval(autoConnectTimerRef.current);
            if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
            if (reconnectTimerRef.current) clearInterval(reconnectTimerRef.current);
        };
    }, []);

    const value = useMemo(() => ({
        credentials, credError, config, globalConfig, isAdmin,
        status, registered, call, activeCliente, error,
        heartbeatOk, lastHeartbeatAt, recording,
        registeredSaida,
        // "pbx" = o ramal está no nosso Asterisk e o servidor grava a ligação
        // inteira; a UI não deve oferecer o botão de gravar do navegador. Vale
        // para a chamada da vez: a de saída ainda é gravada aqui, então lá o
        // botão continua valendo.
        modo: credentials?.modo || "nvoip",
        gravacaoNoServidor: !gravaNoNavegador(credentials, call?.direction),
        overrideDetected, pendingConfirm,
        connect, disconnect, dial, answer, hangup,
        toggleMute, toggleHold, sendDtmf, transfer,
        startRecording: startRecordingManual,
        stopRecording: stopRecordingManual,
        transcreverChamada,
        saveConfig, saveGlobalConfig, refreshGlobalConfig,
        cancelPendingConfirm, dismissOverride,
        setActiveCliente,
    }), [
        credentials, credError, config, globalConfig, isAdmin,
        status, registered, call, activeCliente, error,
        heartbeatOk, lastHeartbeatAt, recording, registeredSaida,
        overrideDetected, pendingConfirm,
        connect, disconnect, dial, answer, hangup,
        toggleMute, toggleHold, sendDtmf, transfer,
        startRecordingManual, stopRecordingManual, transcreverChamada,
        saveConfig, saveGlobalConfig, refreshGlobalConfig,
        cancelPendingConfirm, dismissOverride,
    ]);

    return <SoftphoneContext.Provider value={value}>{children}</SoftphoneContext.Provider>;
};
